import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Loader2, Trash2, Plus, CheckCircle2, Tag as TagIcon } from "lucide-react";
import { toast } from "sonner";
import { API } from "../../App";

const getAuthConfig = () => {
  const token = localStorage.getItem("token");
  return { headers: token ? { Authorization: `Bearer ${token}` } : {} };
};

const TAG_COLORS = [
  "#8b5cf6", "#22c55e", "#ef4444", "#f59e0b", "#3b82f6",
  "#ec4899", "#14b8a6", "#f97316", "#64748b", "#a855f7",
];

/** Convert "h:mm AM/PM" or "HH:mm" to input[type=time] value "HH:mm". */
function toTimeInput(s) {
  if (!s) return "";
  const ampm = String(s).trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
  if (ampm) {
    let h = parseInt(ampm[1], 10);
    if (ampm[3].toUpperCase() === "PM" && h < 12) h += 12;
    if (ampm[3].toUpperCase() === "AM" && h === 12) h = 0;
    return `${String(h).padStart(2, "0")}:${ampm[2]}`;
  }
  const h24 = String(s).trim().match(/^(\d{1,2}):(\d{2})$/);
  if (h24) return `${String(parseInt(h24[1], 10)).padStart(2, "0")}:${h24[2]}`;
  return "";
}

const EMPTY = { title: "", notes: "", scheduled_date: "", scheduled_time: "09:00", duration_hours: 1, assigned_to: "", tag_ids: [], status: "open" };

const DispatchTaskDialog = ({ open, task, defaults, tags, assignees, onClose, onSaved, onTagsChanged }) => {
  const isEdit = !!task;
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showNewTag, setShowNewTag] = useState(false);
  const [newTagName, setNewTagName] = useState("");
  const [newTagColor, setNewTagColor] = useState(TAG_COLORS[0]);
  const [creatingTag, setCreatingTag] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (task) {
      setForm({
        title: task.title || "",
        notes: task.notes || "",
        scheduled_date: task.scheduled_date || "",
        scheduled_time: toTimeInput(task.scheduled_time) || "09:00",
        duration_hours: task.duration_hours || 1,
        assigned_to: task.assigned_to || "",
        tag_ids: task.tag_ids || [],
        status: task.status || "open",
      });
    } else {
      setForm({ ...EMPTY, ...(defaults || {}), scheduled_time: toTimeInput(defaults?.scheduled_time) || "09:00" });
    }
    setShowNewTag(false);
    setNewTagName("");
  }, [open, task, defaults]);

  const toggleTag = (tagId) => {
    setForm((f) => ({
      ...f,
      tag_ids: f.tag_ids.includes(tagId) ? f.tag_ids.filter((t) => t !== tagId) : [...f.tag_ids, tagId],
    }));
  };

  const createTag = async () => {
    if (!newTagName.trim()) { toast.error("Enter a tag name"); return; }
    setCreatingTag(true);
    try {
      const res = await axios.post(`${API}/dispatch/tags`, { name: newTagName.trim(), color: newTagColor }, getAuthConfig());
      toast.success(`Tag "${res.data.name}" created`);
      setForm((f) => ({ ...f, tag_ids: [...f.tag_ids, res.data.tag_id] }));
      setNewTagName("");
      setShowNewTag(false);
      onTagsChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to create tag");
    } finally {
      setCreatingTag(false);
    }
  };

  const save = async () => {
    if (!form.title.trim()) { toast.error("Task title is required"); return; }
    if (!form.scheduled_date) { toast.error("Date is required"); return; }
    setSaving(true);
    try {
      const payload = { ...form, assigned_to: form.assigned_to || null };
      if (isEdit) {
        await axios.put(`${API}/dispatch/tasks/${task.task_id}`, payload, getAuthConfig());
        toast.success("Task updated");
      } else {
        await axios.post(`${API}/dispatch/tasks`, payload, getAuthConfig());
        toast.success("Task created");
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save task");
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setDeleting(true);
    try {
      await axios.delete(`${API}/dispatch/tasks/${task.task_id}`, getAuthConfig());
      toast.success("Task deleted");
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to delete task");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-md" data-testid="dispatch-task-dialog">
        <DialogHeader>
          <DialogTitle className="text-foreground font-['Manrope'] flex items-center gap-2">
            <TagIcon className="w-4 h-4 text-[#FFCC00]" />
            {isEdit ? "Edit Task" : "New Task"}
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-3.5">
          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Task Name *</Label>
            <Input value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
              placeholder="e.g., Follow-up call — spring service quote" className="input-glass h-9" data-testid="task-title-input" />
          </div>

          {/* Tags */}
          <div className="space-y-1.5">
            <Label className="text-xs text-muted-foreground">Tags</Label>
            <div className="flex flex-wrap gap-1.5">
              {(tags || []).map((tag) => {
                const active = form.tag_ids.includes(tag.tag_id);
                return (
                  <button
                    key={tag.tag_id}
                    type="button"
                    onClick={() => toggleTag(tag.tag_id)}
                    className={`px-2 py-0.5 rounded text-[11px] font-semibold text-white transition-all ${active ? "ring-2 ring-offset-1 ring-offset-background" : "opacity-45 hover:opacity-80"}`}
                    style={{ backgroundColor: tag.color, "--tw-ring-color": tag.color }}
                    data-testid={`task-tag-chip-${tag.tag_id}`}
                  >
                    {tag.name}
                  </button>
                );
              })}
              <button
                type="button"
                onClick={() => setShowNewTag((v) => !v)}
                className="px-2 py-0.5 rounded text-[11px] font-medium border border-dashed border-theme text-muted-foreground hover:text-foreground hover:border-foreground/40 transition-colors flex items-center gap-1"
                data-testid="task-new-tag-btn"
              >
                <Plus className="w-3 h-3" /> New tag
              </button>
            </div>
            {showNewTag && (
              <div className="p-2.5 rounded-lg border border-theme bg-theme-surface space-y-2" data-testid="task-new-tag-form">
                <Input value={newTagName} onChange={(e) => setNewTagName(e.target.value)}
                  placeholder="Tag name" className="input-glass h-8 text-sm" maxLength={30} data-testid="task-new-tag-name" />
                <div className="flex items-center gap-1.5 flex-wrap">
                  {TAG_COLORS.map((c) => (
                    <button key={c} type="button" onClick={() => setNewTagColor(c)}
                      className={`w-6 h-6 rounded-full transition-transform ${newTagColor === c ? "ring-2 ring-offset-2 ring-offset-background scale-110" : "hover:scale-110"}`}
                      style={{ backgroundColor: c, "--tw-ring-color": c }}
                      data-testid={`task-new-tag-color-${c.slice(1)}`} />
                  ))}
                </div>
                <Button size="sm" onClick={createTag} disabled={creatingTag} className="btn-primary h-7 text-xs w-full" data-testid="task-add-tag-btn">
                  {creatingTag ? <Loader2 className="w-3 h-3 animate-spin" /> : "Add Tag"}
                </Button>
              </div>
            )}
          </div>

          {/* Assignment + schedule */}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Assign To (dispatcher)</Label>
              <Select value={form.assigned_to || "__none__"} onValueChange={(v) => setForm((f) => ({ ...f, assigned_to: v === "__none__" ? "" : v }))}>
                <SelectTrigger className="input-glass h-9" data-testid="task-tech-select"><SelectValue placeholder="Office / Anyone" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">Office / Anyone</SelectItem>
                  {(assignees || []).map((t) => (
                    <SelectItem key={t.id} value={t.id}>{t.name}{t.role ? ` (${t.role})` : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Date *</Label>
              <Input type="date" value={form.scheduled_date} onChange={(e) => setForm((f) => ({ ...f, scheduled_date: e.target.value }))}
                className="input-glass h-9" data-testid="task-date-input" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Start Time</Label>
              <Input type="time" value={form.scheduled_time} onChange={(e) => setForm((f) => ({ ...f, scheduled_time: e.target.value }))}
                className="input-glass h-9" data-testid="task-time-input" />
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Duration</Label>
              <Select value={String(form.duration_hours)} onValueChange={(v) => setForm((f) => ({ ...f, duration_hours: parseFloat(v) }))}>
                <SelectTrigger className="input-glass h-9" data-testid="task-duration-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {[0.5, 1, 1.5, 2, 3, 4, 6, 8].map((d) => (
                    <SelectItem key={d} value={String(d)}>{d} hour{d !== 1 ? "s" : ""}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1">
            <Label className="text-xs text-muted-foreground">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              placeholder="Details for the dispatcher…" className="input-glass min-h-[64px] text-sm" data-testid="task-notes-input" />
          </div>

          {isEdit && (
            <button
              type="button"
              onClick={() => setForm((f) => ({ ...f, status: f.status === "done" ? "open" : "done" }))}
              className={`w-full flex items-center gap-2 p-2.5 rounded-lg border text-sm transition-colors ${form.status === "done" ? "border-green-500/40 bg-green-500/10 text-green-500" : "border-theme text-muted-foreground hover:text-foreground"}`}
              data-testid="task-status-toggle"
            >
              <CheckCircle2 className="w-4 h-4" />
              {form.status === "done" ? "Completed — click to reopen" : "Mark as done"}
            </button>
          )}
        </div>

        <div className="flex items-center gap-2 pt-2">
          {isEdit && (
            <Button variant="ghost" onClick={remove} disabled={deleting}
              className="text-red-400 hover:text-red-300 hover:bg-red-500/10 px-2.5" data-testid="task-delete-btn">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
            </Button>
          )}
          <div className="flex-1" />
          <Button variant="ghost" onClick={onClose} className="text-muted-foreground">Cancel</Button>
          <Button onClick={save} disabled={saving} className="btn-primary h-9" data-testid="task-save-btn">
            {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving…</> : isEdit ? "Save Changes" : "Create Task"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DispatchTaskDialog;
